import { City, Country, State } from "country-state-city";
import { ErrorMessage, Field, Form, Formik } from "formik";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import React, { useState, useEffect } from "react";
import Select from "react-select";
import * as Yup from "yup";
import { FaSpinner } from "react-icons/fa";
import Popup from "reactjs-popup";
import Loader from "../../../Snippets/Loader";
import axiosInstance from "../../../../Middleware/AxiosInstanceInterceptor";
import { FaCalendarAlt } from "react-icons/fa";


const OrganizationAction = ({
  isPreviewOpen,
  onClose,
  userId,
  roles,
  operation,
  handleClose,
  handleSubmit,
  handleUpdate,
  formData,
  isLoadingUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [updateUserValue, setUpdatedUserValues] = useState([]);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  
  const indiaOption = Country.getAllCountries().find(
    (country) => country.name === "India"
  );

  // Initialize selectedCountry with India
  useEffect(() => {
    if (indiaOption && !selectedCountry) {
      setSelectedCountry(indiaOption);
    }
  }, [indiaOption, selectedCountry]);

  const handleStateChange = (selectedState, setValues) => {
    if (selectedState) {
      setValues((prevValues) => ({
        ...prevValues,
        state_name: selectedState.name || "",
        city_name: "", // Reset city name when state changes
      }));
      setSelectedState(selectedState);
      setSelectedCity(null); // Reset city when state changes
    }
  };

  const handleCityChange = (selectedCity, setValues) => {
    if (selectedCity) {
      setValues((prevValues) => ({
        ...prevValues,
        city_name: selectedCity.name || "",
      }));
      setSelectedCity(selectedCity);
    }
  };

  // Validation schema for organization
  const validationSchema = Yup.object({
    name: Yup.string()
      .required("Entity Name is Required")
      .min(3, "Must be at least 3 characters"),
    email: Yup.string()
      .required("Email is Required")
      .email("Email is Invalid"),
    phone_number: Yup.string()
      .required("Mobile Number is Required")
      .matches(/^[0-9]{10}$/, "Mobile Number is Invalid"),
    whatsapp: Yup.string()
      .matches(/^[0-9]{10}$/, "WhatsApp Number is Invalid")
      .nullable(),
    organizationType: Yup.string().required("Organization Type is Required"),
    panNumber: Yup.string()
      .required("PAN is Required")
      .matches(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, "Invalid PAN format"),
    bar_cid: Yup.string().required("Bar Council ID is Required"),
    auth_person: Yup.string().required("Authorized Person Name is Required"),
    designation: Yup.string().required("Designation is Required"),
    doi: Yup.date()
      .required("Date of Incorporation is Required")
      .max(new Date(), "Date cannot be in the future"),
    address: Yup.string().required("Correspondence Address is Required"),
    state_name: Yup.string().required("State is Required"),
    city_name: Yup.string().required("City is Required"),
  });

  // Organization Type options
  const organizationTypeOptions = [
    {
      label: "Association of Persons",
      value: "association_of_persons",
    },
    {
      label: "Body of Individuals",
      value: "body_of_individuals",
    },
    {
      label: "Company",
      value: "company",
    },
    {
      label: "Trust",
      value: "trust",
    },
    {
      label: "Limited Liability Partnership",
      value: "limited_liability_partnership",
    },
    {
      label: "Firm",
      value: "firm",
    },
    {
      label: "Government",
      value: "government",
    },
    {
      label: "Hindu Undivided Family",
      value: "hindu_undivided_family",
    },
    {
      label: "Artificial Juridical Person",
      value: "artificial_juridical_person",
    },
    {
      label: "Local Authority",
      value: "local_authority",
    },
  ];

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setFilteredOptions(organizationTypeOptions);
    }
  };

  const handleOptionClick = (option, setFieldValue) => {
    setFieldValue("organizationType", option.value);
    setIsOpen(false);
  };

  // Function to handle form submission
  const handleFormSubmit = async (values, { setSubmitting, resetForm }) => {
    setIsLoading(true);
    try {
      const authToken = localStorage.getItem("authToken") || document.cookie.replace(/(?:(?:^|.*;\s*)authToken\s*=\s*([^;]*).*$)|^.*$/, "$1");
      
      // Prepare headers - For organization, module-id should be "entity" 
      // based on your previous instruction
      const headers = {
        "Content-Type": "application/json",
        "module-id": "entity", // Organization uses "entity" module-id
        ...(authToken && { Authorization: `Bearer ${authToken}` })
      };

      // Prepare organization request body
      const requestBody = {
        email: values.email,
        phone_number: values.phone_number || values.whatsapp,
        role: "entity",
        name: values.name,
        address: {
          country: "IN", // Fixed as India
          state: selectedState?.isoCode || "",
          city: selectedCity?.name || "",
          pin_Code: values.pincode || 0,
          correspondence_address: values.address
        },
        personal: {
          nationality: "Indian",
          userType: "party",
          organizationType: values.organizationType,
          pancard: values.panNumber,
          doi: new Date(values.doi).toISOString(),
          bar_Council_Id: values.bar_cid,
          gst_number: values.gst_number || "",
          authorised_peronal_name: values.auth_person,
          designation: values.designation
        }
      };

      // Make API call
      const response = await axiosInstance.post(
        "/user/create",
        requestBody,
        { headers }
      );

      if (response.status === 200 || response.status === 201) {
        alert('Organization created successfully!');
        resetForm();
        handleClose();
        if (handleSubmit) {
          handleSubmit(response.data);
        }
      }
    } catch (error) {
      console.error("Error creating organization:", error);
      const errorMessage = error.response?.data?.message || error.message || "Unknown error";
      alert(`Failed to create organization. Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  // Get initial values
  const getInitialValues = () => {
    if (isEditing && updateUserValue) {
      return updateUserValue;
    }
    
    return {
      name: "",
      email: "",
      phone_number: "",
      whatsapp: "",
      organizationType: "",
      panNumber: "",
      bar_cid: "",
      auth_person: "",
      designation: "",
      doi: "",
      gst_number: "",
      address: "",
      state_name: "",
      city_name: "",
      pincode: ""
    };
  };

  return (
    <>
      <Popup open={isPreviewOpen} modal closeOnDocumentClick={false}>
        {(close) => (
          <div
            className="popup-style1"
            style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}
          >
            {isLoading ? (
              <Loader />
            ) : (
            <div className="container pt-6 sm:pt-10 px-4 sm:px-8 mx-auto rounded-md max-h-screen overflow-y-auto">
  <div className="w-full max-w-5xl mx-auto">
                  <Formik
                    initialValues={getInitialValues()}
                    validationSchema={validationSchema}
                    onSubmit={handleFormSubmit}
                    enableReinitialize
                  >
                    {(formikProps) => (
                      <Form>
                        <div className="bg-bg-form px-16 pt-10 pb-16 text-tbl-txt-clr">
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => {
                                handleClose();
                                close();
                                setUpdatedUserValues(null);
                              }}
                              className="text-2xl font-bold"
                            >
                              X
                            </button>
                          </div>
                          <h2 className="text-xl font-semibold pb-6">
                            {isEditing ? "Update Organization" : "Add Organization"}
                          </h2>

                          {/* Entity Name, Organization type */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="mb-4">
                              <label htmlFor="name" className="label-style">
                                Entity Name<span className="required">*</span>
                              </label>
                              <Field
                                type="text"
                                placeholder="Enter Entity Name"
                                id="name"
                                name="name"
                                className="input-style"
                              />
                              <ErrorMessage
                                name="name"
                                component="div"
                                className="error text-red-600 text-sm mt-1"
                              />
                            </div>

                            {/* Organization Type */}
                            <div className="mb-4 relative">
                              <label htmlFor="organizationType" className="label-style">
                                Organization Type<span className="required">*</span>
                              </label>
                              <div className="relative">
                                <Field
                                  type="text"
                                  id="organizationType"
                                  name="organizationType"
                                  placeholder="Select Organization Type"
                                  className="dropdown-input input-style w-full"
                                  onClick={toggleDropdown}
                                  readOnly
                                  value={
                                    organizationTypeOptions.find(
                                      opt => opt.value === formikProps.values.organizationType
                                    )?.label || ""
                                  }
                                />
                                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                  </svg>
                                </div>
                              </div>
                              {isOpen && (
                                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                  {filteredOptions.map((option, index) => (
                                    <div
                                      key={index}
                                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                                      onClick={() =>
                                        handleOptionClick(
                                          option,
                                          formikProps.setFieldValue
                                        )
                                      }
                                    >
                                      {option.label}
                                    </div>
                                  ))}
                                </div>
                              )}
                              <ErrorMessage
                                name="organizationType"
                                component="div"
                                className="text-red-600 text-sm mt-1"
                              />
                            </div>
                          </div>

                          {/* PAN, Bar council ID */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="mb-4">
                              <label htmlFor="panNumber" className="label-style">
                                PAN<span className="required">*</span>
                              </label>
                              <Field
                                type="text"
                                placeholder="Enter PAN"
                                id="panNumber"
                                name="panNumber"
                                className="input-style"
                                maxLength="10"
                                style={{ textTransform: "uppercase" }}
                              />
                              <ErrorMessage
                                name="panNumber"
                                component="div"
                                className="error text-red-600 text-sm mt-1"
                              />
                            </div>

                            <div className="mb-4">
                              <label htmlFor="bar_cid" className="label-style">
                                Bar Council ID<span className="required">*</span>
                              </label>
                              <Field
                                type="text"
                                id="bar_cid"
                                name="bar_cid"
                                placeholder="Enter Bar Council ID"
                                className="input-style"
                              />
                              <ErrorMessage
                                name="bar_cid"
                                component="div"
                                className="error text-red-600 text-sm mt-1"
                              />
                            </div>
                          </div>

                          {/* Email, whatsapp */}
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="mb-4">
                              <label htmlFor="email" className="label-style">
                                Email ID<span className="required">*</span>
                              </label>
                              <Field
                                type="email"
                                id="email"
                                name="email"
                                placeholder="Enter email Id"
                                className="input-style"
                              />
                              <ErrorMessage
                                name="email"
                                component="div"
                                className="error text-red-600 text-sm mt-1"
                              />
                            </div>

                            <div className="mb-4">
                              <label htmlFor="phone_number" className="label-style">
                                Phone Number<span className="required">*</span>
                              </label>
                              <Field
                                type="text"
                                id="phone_number"
                                name="phone_number"
                                placeholder="Enter phone number"
                                className="input-style"
                                maxLength="10"
                              />
                              <ErrorMessage
                                name="phone_number"
                                component="div"
                                className="error text-red-600 text-sm mt-1"
                              />
                            </div>
                          </div>

                          {/* Authorized person, Designation */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="mb-4">
                              <label htmlFor="auth_person" className="label-style">
                                Authorized Person Name<span className="required">*</span>
                              </label>
                              <Field
                                type="text"
                                placeholder="Enter Authorized Person Name"
                                id="auth_person"
                                name="auth_person"
                                className="input-style"
                              />
                              <ErrorMessage
                                name="auth_person"
                                component="div"
                                className="error text-red-600 text-sm mt-1"
                              />
                            </div>

                            <div className="mb-4">
                              <label htmlFor="designation" className="label-style">
                                Designation<span className="required">*</span>
                              </label>
                              <Field
                                type="text"
                                id="designation"
                                name="designation"
                                placeholder="Enter Designation"
                                className="input-style"
                              />
                              <ErrorMessage
                                name="designation"
                                component="div"
                                className="error text-red-600 text-sm mt-1"
                              />
                            </div>
                          </div>

                          {/* Date of Incorporation, GST Number */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
<div className="mb-4 cursor-pointer">
  <label className="label-style">
    Date of Incorporation<span className="required">*</span>
  </label>

  <div className="relative w-full">

    <DatePicker
      selected={
        formikProps.values.dob
          ? new Date(formikProps.values.dob)
          : null
      }
      onChange={(date) => formikProps.setFieldValue("dob", date)}
      dateFormat="dd/MM/yyyy"
      maxDate={new Date()}
      placeholderText="Select Date of Birth"
      showYearDropdown
      showMonthDropdown
      dropdownMode="select"
      wrapperClassName="w-full"
      className="input-style w-full pr-12"
    />

    {/* Calendar Icon */}
    <FaCalendarAlt
      className="absolute right-3 top-[15px] text-gray-400 pointer-events-none cursor-pointer"
      size={18}
    />

  </div>

  <ErrorMessage
    name="dob"
    component="div"
    className="error text-red-600 text-sm mt-1"
  />
</div>

                            <div className="mb-4">
                              <label htmlFor="gst_number" className="label-style">
                                GST Number
                              </label>
                              <Field
                                type="text"
                                id="gst_number"
                                name="gst_number"
                                placeholder="Enter GST Number"
                                className="input-style"
                              />
                            </div>
                          </div>

                          {/* WhatsApp */}
                          <div className="mb-4">
                            <label htmlFor="whatsapp" className="label-style">
                              WhatsApp Number
                            </label>
                            <Field
                              type="text"
                              id="whatsapp"
                              name="whatsapp"
                              placeholder="Enter WhatsApp number"
                              className="input-style"
                              maxLength="10"
                            />
                            <ErrorMessage
                              name="whatsapp"
                              component="div"
                              className="error text-red-600 text-sm mt-1"
                            />
                          </div>

                          {/* Country */}
                          <div className="mb-4">
                            <label htmlFor="country_name" className="label-style">
                              Country<span className="required">*</span>
                            </label>
                            <Select
                              styles={{
                                control: (baseStyles, state) => ({
                                  ...baseStyles,
                                  borderColor: state.isFocused ? "#3B82F6" : "#e2e8f0",
                                  border: "2px solid #e2e8f0",
                                  padding: "4px",
                                  marginTop: "1px",
                                }),
                              }}
                              name="country_name"
                              id="country_name"
                              options={[indiaOption]}
                              getOptionLabel={(option) => option.name}
                              getOptionValue={(option) => option.name}
                              value={selectedCountry}
                              isDisabled={true}
                            />
                          </div>

                          {/* State, City, address */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <div className="mb-4">
                                <label htmlFor="state_name" className="label-style">
                                  State<span className="required">*</span>
                                </label>
                                <Select
                                  name="state_name"
                                  id="state_name"
                                  styles={{
                                    control: (baseStyles, state) => ({
                                      ...baseStyles,
                                      borderColor: state.isFocused ? "#3B82F6" : "#e2e8f0",
                                      border: "2px solid #e2e8f0",
                                      padding: "4px",
                                      marginTop: "1px",
                                    }),
                                  }}
                                  options={State?.getStatesOfCountry("IN")}
                                  getOptionLabel={(option) => option.name}
                                  getOptionValue={(option) => option.name}
                                  value={selectedState}
                                  onChange={(selectedOption) =>
                                    handleStateChange(selectedOption, formikProps.setValues)
                                  }
                                />
                                <ErrorMessage
                                  name="state_name"
                                  component="div"
                                  className="error text-red-600 text-sm mt-1"
                                />
                              </div>

                              <div className="mb-4">
                                <label htmlFor="city_name" className="label-style">
                                  City<span className="required">*</span>
                                </label>
                                <Select
                                  name="city_name"
                                  id="city_name"
                                  styles={{
                                    control: (baseStyles, state) => ({
                                      ...baseStyles,
                                      borderColor: state.isFocused ? "#3B82F6" : "#e2e8f0",
                                      border: "2px solid #e2e8f0",
                                      padding: "4px",
                                      marginTop: "1px",
                                    }),
                                  }}
                                  options={City.getCitiesOfState(
                                    selectedState?.countryCode,
                                    selectedState?.isoCode
                                  )}
                                  getOptionLabel={(option) => option.name}
                                  getOptionValue={(option) => option.name}
                                  value={selectedCity}
                                  onChange={(selectedOption) =>
                                    handleCityChange(selectedOption, formikProps.setValues)
                                  }
                                  isDisabled={!selectedState}
                                />
                                <ErrorMessage
                                  name="city_name"
                                  component="div"
                                  className="error text-red-600 text-sm mt-1"
                                />
                              </div>

                              <div className="mb-4">
                                <label htmlFor="pincode" className="label-style">
                                  Pincode
                                </label>
                                <Field
                                  type="text"
                                  id="pincode"
                                  name="pincode"
                                  placeholder="Enter pincode"
                                  className="input-style"
                                  maxLength="6"
                                />
                              </div>
                            </div>

                            <div className="mb-4">
                              <label htmlFor="address" className="label-style">
                                Correspondence Address<span className="required">*</span>
                              </label>
                              <Field
                                as="textarea"
                                placeholder="Enter correspondence address"
                                id="address"
                                name="address"
                                className="input-style h-[126px] w-full"
                                rows="4"
                              />
                              <ErrorMessage
                                name="address"
                                component="div"
                                className="error text-red-600 text-sm mt-1"
                              />
                            </div>
                          </div>

                          <div className="flex justify-center mt-10">
                            <button
                              type="submit"
                              className="btn-style2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                              disabled={isLoading || formikProps.isSubmitting}
                            >
                              {isLoading || formikProps.isSubmitting ? (
                                <FaSpinner className="animate-spin mx-auto" />
                              ) : (
                                "Add Organization"
                              )}
                            </button>
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            )}
          </div>
        )}
      </Popup>
    </>
  );
};

export default OrganizationAction;